import axios from 'axios';

const API_BASE = process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os';

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('scenario_os_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const u = err.config?.url || '';
    const isAuthEndpoint =
      u.includes('/learner/login') ||
      u.includes('/learner/register') ||
      u.includes('/learner/forgot-password') ||
      u.includes('/learner/reset-password') ||
      u.includes('/learner/sso/');
    if (err.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('scenario_os_token');
      localStorage.removeItem('scenario_os_learner');
      // Hard navigation bypasses react-router, so we must prepend the
      // SPA basename (e.g. /training in production, '' in local dev) —
      // otherwise the user lands on /login at the nginx layer and gets
      // a "404 Not Found" instead of the AuthPage.
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      window.location.href = `${base}/login`;
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/learner/register', data),
  login: (data) => api.post('/learner/login', data),
  // Host-scoped SSO cookie (set at login, shared with Championship DNA). The custom header is the
  // same-site proof the server requires.
  ssoSession: () => api.post('/learner/sso/session', null, { headers: { 'X-Requested-With': 'fg-sso' } }),
  ssoLogout: () => api.post('/learner/sso/logout', null, { headers: { 'X-Requested-With': 'fg-sso' } }),
  forgotPassword: (email) => api.post('/learner/forgot-password', { email }),
  resetPassword: (token, newPassword) =>
    api.post('/learner/reset-password', { token, new_password: newPassword }),
  changePassword: (currentPassword, newPassword) =>
    api.post('/learner/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  getProfile: () => api.get('/learner/profile'),
  updateProfile: (data) => api.patch('/learner/profile', data),
  refreshToken: () => api.post('/learner/token/refresh'),
  enrollDomain: (domain) => api.post('/learner/enroll', { domain }),
  upgradeSubscription: (learnerId, domainId, paymentMethodId) =>
    api.post('/marketplace/subscriptions/upgrade', { learner_id: learnerId, domain_id: domainId, payment_method_id: paymentMethodId }),
  getSubscriptions: (learnerId) => api.get(`/marketplace/subscriptions/${learnerId}`),
};

export const sessionAPI = {
  start: (data) => api.post('/session/start', data),
  advance: (sessionId) => api.post(`/session/${sessionId}/advance`),
  nextContent: (sessionId) => api.post(`/session/${sessionId}/next-content`),
  respond: (sessionId, data) => api.post(`/session/${sessionId}/respond`, data),
  end: (sessionId) => api.post(`/session/${sessionId}/end`),
  getDebrief: (sessionId) => api.get(`/session/${sessionId}/debrief`),
  pause: (sessionId, data) => api.post(`/session/${sessionId}/pause`, data || { video_position_ms: 0 }),
  resume: (sessionId) => api.post(`/session/${sessionId}/resume`),
  getResumable: () => api.get('/session/resumable'),
  getAgentStatus: (sessionId) => api.get(`/session/${sessionId}/agent-status`),
};

export const curriculumAPI = {
  listDomains: () => api.get('/curriculum/domains'),
  listTracks: (domain, params = {}) => api.get(`/curriculum/${domain}/tracks`, { params }),
  getTrackProgress: (domain, trackId, params = {}) => api.get(`/curriculum/${domain}/tracks/${trackId}/progress`, { params }),
  listTrackScenarios: (domain, trackId) => api.get(`/curriculum/${domain}/tracks/${trackId}/scenarios`),
  recommend: (domain) => api.get(`/curriculum/${domain}/recommend`),
  getProgressSummary: () => api.get('/curriculum/progress/summary'),
  getCertificate: (domain) => api.get(`/curriculum/${domain}/certificate`),
  getShareLinks: (domain, certId) => api.get(`/curriculum/${domain}/certificate/${certId}/share`),
  getPreference: (domain) => api.get(`/curriculum/preferences/${domain}`),
  setPreference: (domain, difficulty) => api.patch(`/curriculum/preferences/${domain}`, { difficulty }),
  getPretestStatus: (domain, moduleId) => api.get(`/curriculum/${domain}/modules/${moduleId}/pretest-status`),
  completePretest: (domain, moduleId, data) => api.post(`/curriculum/${domain}/modules/${moduleId}/pretest-complete`, data),
};

export const gamificationAPI = {
  getProfile: (domain) => api.get(`/gamification/profile/${domain}`),
  getBadges: (domain) => api.get(`/gamification/badges/${domain}`),
  getAvailableBadges: (domain) => api.get(`/gamification/badges/${domain}/available`),
  getLeaderboard: (params) => api.get('/gamification/leaderboard', { params }),
};

export const analyticsAPI = {
  getCognitiveState: (sessionId, learnerId) =>
    api.get(`/analytics/cognitive-state/${sessionId}`, { params: { learner_id: learnerId } }),
  getTimingProfile: (learnerId, domain) => api.get(`/analytics/timing-profile/${learnerId}/${domain}`),
  getAdaptation: (sessionId) => api.get(`/analytics/adaptation/${sessionId}`),
};

export const videoAPI = {
  start: (data) => api.post('/video/start', data),
  updatePosition: (data) => api.post('/video/position', data),
  respond: (data) => api.post('/video/respond', data),
  getProgress: (sessionId) => api.get(`/video/progress/${sessionId}`),
};

// Slide-deck learner runtime — mirrors videoAPI for the new Tier-2
// content_type. The backend lives in
// ``core/scenario/api/slide_deck_routes.py`` and the auth model is
// identical (Bearer token interceptor above).
export const slideDeckAPI = {
  start: (data) => api.post('/slide-deck/start', data),
  navigate: (data) => api.post('/slide-deck/navigate', data),
  respond: (data) => api.post('/slide-deck/respond', data),
  getProgress: (sessionId) => api.get(`/slide-deck/progress/${sessionId}`),
  // Public-ish file fetch used by SlideDeckPlayer to load the PDF
  // bytes into pdfjs-dist. Coaches see this through the designer.
  // Returns the canonical URL so the player can hand it to pdfjs's
  // ``getDocument({ url })``.
  fileUrl: (deckId) =>
    `${API_BASE.replace(/\/$/, '')}/coach/slide-deck/file/${deckId}`,
};

// Coach-side slide-deck authoring — upload PPTX / PDF + manage
// interactions per slide. Mirrors videoCoachAPI shape.
export const slideDeckCoachAPI = {
  uploadFile: (file, { domainId, sceneId, title, onProgress } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (domainId) fd.append('domain_id', domainId);
    if (sceneId) fd.append('scene_id', sceneId);
    if (title) fd.append('title', title);
    return api.post('/coach/slide-deck/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Generous timeout — a multi-MB PPTX needs a LibreOffice
      // headless conversion on the server side that can take 30-90s
      // for decks with embedded media.
      timeout: 15 * 60 * 1000,
      onUploadProgress: onProgress,
    });
  },
  listMine: (params = {}) => api.get('/coach/slide-deck/recordings', { params }),
  get: (deckId) => api.get(`/coach/slide-deck/${deckId}`),
  remove: (deckId) => api.delete(`/coach/slide-deck/${deckId}`),
};

// Tenant branding — Scenario OS (Tier 2) learner-app theming.
// Resolves through CacheManager → tenant_branding config_store doc.
// The frontend BrandingProvider calls these on bootstrap to apply CSS
// custom properties (--accent, --bg-primary, …) for the active tenant.
export const brandingAPI = {
  getDefault: () => api.get('/branding'),
  getForClient: (clientId) => api.get(`/branding/${clientId}`),
  // Coach/admin write — only allowed when JWT.tenant matches client_id
  // (or the caller is platform_admin). Backed by the same config_store
  // doc that the seed script writes.
  updateForClient: (clientId, payload) => api.put(`/branding/${clientId}`, payload),
};

// Tenant registry — the canonical list of "what tenants exist".
// Backed by cfg:client:scenario_os:v1:tenant_registry. Coaches see
// their own tenant + any tenant with show_in_login=true; admins see
// all.
export const tenantAPI = {
  list: () => api.get('/tenants'),
  update: (clientId, payload) => api.put(`/tenants/${clientId}`, payload),
};

// Coach-side video authoring (Video Scenes)
export const videoCoachAPI = {
  // Register a pasted URL (YouTube / Vimeo / direct MP4).
  submitURL: ({ url, domainId, sceneId, title, durationMs, thumbnailUrl }) =>
    api.post('/coach/video/url', {
      url,
      domain_id: domainId,
      scene_id: sceneId || null,
      title: title || null,
      duration_ms: durationMs || null,
      thumbnail_url: thumbnailUrl || null,
    }),
  // Multipart upload routed through StorageRouter on the backend.
  //
  // ``onProgress`` is an optional axios ``onUploadProgress`` handler — the
  // caller receives ``ProgressEvent`` with ``loaded`` / ``total`` so we
  // can render a real progress bar instead of a fake setInterval tick.
  // Large MP4s (60+ MB) take real time on residential uplinks and a
  // stuck-looking bar is the #1 source of "the upload is broken" tickets
  // when in fact the bytes are still in flight.
  uploadFile: (file, { domainId, sceneId, title, onProgress } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (domainId) fd.append('domain_id', domainId);
    if (sceneId) fd.append('scene_id', sceneId);
    if (title) fd.append('title', title);
    return api.post('/coach/video/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Long enough for a ~500 MB upload at 5 Mbit/s (full-game film) plus
      // the GCS round-trip on the server side. Falls back to the axios
      // default (no timeout) when undefined would be ambiguous.
      timeout: 15 * 60 * 1000,
      onUploadProgress: onProgress,
    });
  },
  listMine: (params = {}) => api.get('/coach/video/recordings', { params }),
  get: (videoId) => api.get(`/coach/video/${videoId}`),
  remove: (videoId) => api.delete(`/coach/video/${videoId}`),
};

export const contentAPI = {
  generateScenario: (data) => api.post('/content/generate-scenario', data),
  generateQuestions: (data) => api.post('/content/generate-questions', data),
  generateCurriculum: (data) => api.post('/content/generate-curriculum', data),
};

export const teachingAPI = {
  start: (sessionId, moduleId) =>
    api.post(`/teaching/${sessionId}/start`, { module_id: moduleId }),
  nextSegment: (sessionId) =>
    api.post(`/teaching/${sessionId}/next-segment`),
  getProgress: (sessionId) =>
    api.get(`/teaching/${sessionId}/progress`),
};

export const contentMgmtAPI = {
  list: (domain, contentType, params = {}) =>
    api.get(`/content-mgmt/${domain}/${contentType}`, { params }),
  get: (domain, contentType, itemId) =>
    api.get(`/content-mgmt/${domain}/${contentType}/${itemId}`),
  create: (domain, contentType, data) =>
    api.post(`/content-mgmt/${domain}/${contentType}`, { data }),
  update: (domain, contentType, itemId, updates) =>
    api.put(`/content-mgmt/${domain}/${contentType}/${itemId}`, { updates }),
  publish: (domain, contentType, itemId) =>
    api.post(`/content-mgmt/${domain}/${contentType}/${itemId}/publish`),
  archive: (domain, contentType, itemId) =>
    api.post(`/content-mgmt/${domain}/${contentType}/${itemId}/archive`),
  remove: (domain, contentType, itemId) =>
    api.delete(`/content-mgmt/${domain}/${contentType}/${itemId}`),
  seed: (domain) =>
    api.post(`/content-mgmt/${domain}/seed`),
  ttsPreview: (text, voice = 'alloy') =>
    api.post('/content-mgmt/tts-preview', { text, voice }),
};

export const participantAPI = {
  getParticipant: () => api.get('/participants/me'),
  listCharacters: (learnerId, domainId) =>
    api.get(`/participants/me/characters/${domainId}`),
  getAvailableClasses: (domainId) =>
    api.get(`/participants/me/characters/${domainId}/available-classes`),
  getSubscriptions: (learnerId, domainId) =>
    api.get(`/participants/me/characters/${domainId}/subscriptions`),
  createCharacter: (learnerId, data) =>
    api.post(`/participants/me/characters/${data.domain_id}`, {
      character_class: data.character_class,
      display_name: data.display_name,
    }),
  activateCharacter: (learnerId, characterId, domainId, specialtyId) =>
    api.post(`/participants/me/characters/${domainId}/activate/${characterId}`, {
      specialty_id: specialtyId || null,
    }),
  deleteCharacter: (learnerId, characterId, domainId) =>
    api.delete(`/participants/me/characters/${domainId}/${characterId}`),
  unlockSpecialty: (learnerId, characterId, domainId, specialtyId) =>
    api.post(`/participants/me/characters/${domainId}/activate/${characterId}`, {
      specialty_id: specialtyId,
    }),
  buildSessionContext: (learnerId, data) =>
    api.post(`/participants/${learnerId}/session-context`, data),
  // Enrollment requests (learner side)
  requestEnrollment: (domain, note) =>
    api.post('/participants/me/enrollment-requests', { domain, note }),
  getMyEnrollmentRequests: () =>
    api.get('/participants/me/enrollment-requests'),
};

export const marketplaceAPI = {
  listDomains: (params) => api.get('/marketplace/domains', { params }),
  getDomain: (domainId) => api.get(`/marketplace/domains/${domainId}`),
  enroll: (data) => api.post('/marketplace/enroll', data),
  getSubscriptions: (learnerId) => api.get(`/marketplace/subscriptions/${learnerId}`),
  checkPrerequisites: (domainId, data) =>
    api.post(`/marketplace/domains/${domainId}/check-prerequisites`, data),
  unlockSpecialty: (domainId, specialtyId, data) =>
    api.post(`/marketplace/domains/${domainId}/specialties/${specialtyId}/unlock`, data),
};

export const coachAPI = {
  // Tutorials
  listTutorials: (params = {}) => api.get('/coach/tutorials', { params }),
  getTutorial: (id) => api.get(`/coach/tutorials/${id}`),
  createTutorial: (data) => api.post('/coach/tutorials', data),
  updateTutorial: (id, data) => api.patch(`/coach/tutorials/${id}`, data),
  deleteTutorial: (id) => api.delete(`/coach/tutorials/${id}`),
  publishTutorial: (id) => api.post(`/coach/tutorials/${id}/publish`),
  addSegment: (id, data) => api.post(`/coach/tutorials/${id}/segments`, data),
  // Curriculum tracks
  listTracks: (domain) => api.get(`/coach/curriculum/${domain}/tracks`),
  createTrack: (domain, data) => api.post(`/coach/curriculum/${domain}/tracks`, data),
  updateTrack: (domain, trackId, data) => api.put(`/coach/curriculum/${domain}/tracks/${trackId}`, data),
  deleteTrack: (domain, trackId) => api.delete(`/coach/curriculum/${domain}/tracks/${trackId}`),
  // Curriculum modules
  listModules: (domain, trackId) => api.get(`/coach/curriculum/${domain}/tracks/${trackId}/modules`),
  createModule: (domain, trackId, data) => api.post(`/coach/curriculum/${domain}/tracks/${trackId}/modules`, data),
  updateModule: (domain, trackId, moduleId, data) => api.put(`/coach/curriculum/${domain}/tracks/${trackId}/modules/${moduleId}`, data),
  deleteModule: (domain, trackId, moduleId) => api.delete(`/coach/curriculum/${domain}/tracks/${trackId}/modules/${moduleId}`),
  // Learners
  listLearners: (domain) => api.get('/coach/learners', { params: domain ? { domain } : {} }),
  getLearnerStats: (learnerId, domain) => api.get(`/coach/learners/${learnerId}/stats/${domain}`),
  enrollLearner: (payload) => api.post('/coach/learners/enroll', payload),
  setLearnerSubscriptionTier: (learnerId, domain, tier) =>
    api.patch(`/coach/learners/${learnerId}/subscription`, { domain, tier }),
  // Enrollment requests (coach side)
  listEnrollmentRequests: (params = {}) => api.get('/coach/enrollment-requests', { params }),
  approveEnrollment: (requestId) => api.post(`/coach/enrollment-requests/${requestId}/approve`),
  rejectEnrollment: (requestId, reason) => api.post(`/coach/enrollment-requests/${requestId}/reject`, { reason }),
  // Content library
  listContent: (domain, contentType, params = {}) => api.get(`/coach/content/${domain}/${contentType}`, { params }),
  // Engines
  listEngines: () => api.get('/coach/engines'),
  getWorkspaceConfig: (domain, specialtyId) => api.get('/coach/workspace-config', { params: { domain_id: domain, specialty_id: specialtyId } }),
  // My entitlements
  listMyDomains: () => api.get('/coach/my-domains'),
  // Admin: user management (platform admin only)
  adminListUsers: (params = {}) => api.get('/coach/admin/users', { params }),
  adminUpdateUserRole: (learnerId, role) => api.patch(`/coach/admin/users/${learnerId}/role`, { role }),
  // Domain coach management (admin or domain_admin)
  listDomainCoaches: (domain) => api.get(`/coach/domain/${domain}/coaches`),
  addDomainCoach: (domain, payload) => api.post(`/coach/domain/${domain}/coaches`, payload),
  updateDomainCoachRole: (domain, coachId, domainRole) => api.patch(`/coach/domain/${domain}/coaches/${coachId}`, { domain_role: domainRole }),
  removeDomainCoach: (domain, coachId) => api.delete(`/coach/domain/${domain}/coaches/${coachId}`),
  // Plays CRUD
  listPlays: (domain, params = {}) => api.get(`/coach/domain/${domain}/plays`, { params }),
  createPlay: (domain, data) => api.post(`/coach/domain/${domain}/plays`, data),
  updatePlay: (domain, playId, data) => api.put(`/coach/domain/${domain}/plays/${playId}`, data),
  deletePlay: (domain, playId) => api.delete(`/coach/domain/${domain}/plays/${playId}`),
  // Coverages CRUD
  listCoverages: (domain, params = {}) => api.get(`/coach/domain/${domain}/coverages`, { params }),
  createCoverage: (domain, data) => api.post(`/coach/domain/${domain}/coverages`, data),
  updateCoverage: (domain, coverageId, data) => api.put(`/coach/domain/${domain}/coverages/${coverageId}`, data),
  deleteCoverage: (domain, coverageId) => api.delete(`/coach/domain/${domain}/coverages/${coverageId}`),
  // Character class management
  listCharacterClasses: (domain) => api.get(`/coach/domain/${domain}/character-classes`),
  upsertCharacterClass: (domain, classId, data) => api.put(`/coach/domain/${domain}/character-classes/${classId}`, data),
  deleteCharacterClass: (domain, classId) => api.delete(`/coach/domain/${domain}/character-classes/${classId}`),
  // Domain settings (character_archetypes_enabled, etc.)
  patchDomainSettings: (domain, data) => api.patch(`/coach/domain/${domain}`, data),
  // AI coach personas
  listPersonas: (domain) => api.get(`/coach/domain/${domain}/personas`),
  // Curriculum with auto-import from YAML
  listTracksWithImport: (domain) => api.get(`/coach/curriculum/${domain}/tracks-with-import`),
  // Tutorial publish to roster
  publishTutorialToRoster: (id, rosterId) =>
    api.post(`/coach/tutorials/${id}/publish`, null, { params: rosterId ? { roster_id: rosterId } : {} }),
  unpublishTutorial: (id) => api.post(`/coach/tutorials/${id}/unpublish`),
  // Segment editing
  updateSegment: (tutorialId, segmentId, data) =>
    api.patch(`/coach/tutorials/${tutorialId}/segments/${segmentId}`, data),
  deleteSegment: (tutorialId, segmentId) =>
    api.delete(`/coach/tutorials/${tutorialId}/segments/${segmentId}`),
};

// Scene Designer (canonical play/scene authoring)
export const sceneAPI = {
  list: (params = {}) => api.get('/coach/scenes', { params }),
  get: (sceneId) => api.get(`/coach/scenes/${sceneId}`),
  create: (data) => api.post('/coach/scenes', data),
  update: (sceneId, data) => api.patch(`/coach/scenes/${sceneId}`, data),
  remove: (sceneId) => api.delete(`/coach/scenes/${sceneId}`),
  duplicate: (sceneId) => api.post(`/coach/scenes/${sceneId}/duplicate`),
  // override: { override_pedagogy: true, override_reason } publishes a draft that
  // fails the TeachIQ Content Quality Standard; the reason is recorded.
  publish: (sceneId, override) => api.post(`/coach/scenes/${sceneId}/publish`, override || undefined),
  unpublish: (sceneId) => api.post(`/coach/scenes/${sceneId}/unpublish`),
  // Throw away the unpublished pending version of a live scene (the live scene is unchanged).
  discardPending: (sceneId) => api.delete(`/coach/scenes/${sceneId}/pending`),
  suggestQuestions: (sceneId, data) =>
    api.post(`/coach/scenes/${sceneId}/suggest-questions`, data),
  // Probe whether Suggest-with-AI is reachable end-to-end. The route
  // mirrors the AgentFactory resolution path the real call takes, so
  // the UI can disable the button on mount when the platform stack is
  // unavailable rather than letting the coach trigger a 503.
  suggestQuestionsHealth: () =>
    api.get('/coach/scenes/suggest-questions/health'),
  // Decision-point question template lookup (used by Preview).
  getQuestionTemplate: (templateId) =>
    api.get(`/coach/question-templates/${templateId}`),
  // Content block lookup/edit (used by GenericSceneEditorPage to surface
  // reading-scene prose alongside scene metadata).
  getContentBlock: (blockId) => api.get(`/coach/content-blocks/${blockId}`),
  updateContentBlock: (blockId, data) =>
    api.patch(`/coach/content-blocks/${blockId}`, data),
  // Domain pack metadata
  getCoordSystem: (domainId) => api.get(`/coach/domain/${domainId}/coordinate-system`),
  listFormations: (domainId) => api.get(`/coach/domain/${domainId}/formations`),
  listActorRoles: (domainId) => api.get(`/coach/domain/${domainId}/actor-roles`),
  listSceneTypes: (domainId) => api.get(`/coach/domain/${domainId}/scene-types`),
  /** Inclusive ContentFocus × DeliveryModality chooser vocabulary. */
  getSceneTypeChooserConfig: () => api.get('/coach/scene-type-chooser-config'),
  listTimelineVocabulary: (domainId) => api.get(`/coach/domain/${domainId}/timeline-vocabulary`),
  // Sprint 4 — JSON export / import
  export: (sceneId) => api.get(`/coach/scenes/${sceneId}/export`),
  import: (data) => api.post(`/coach/scenes/import`, data),
};

// Sprint 3 — Reusable named route trees ("Curl-Flat", "Verticals", etc.)
export const routeTreeAPI = {
  list: (params = {}) => api.get('/coach/route-trees', { params }),
  get: (routeTreeId) => api.get(`/coach/route-trees/${routeTreeId}`),
  create: (data) => api.post('/coach/route-trees', data),
  update: (routeTreeId, data) => api.patch(`/coach/route-trees/${routeTreeId}`, data),
  remove: (routeTreeId) => api.delete(`/coach/route-trees/${routeTreeId}`),
};

// Sprint 4 — Coach-owned playbook binders (collections of scenes).
export const playbookAPI = {
  list: (params = {}) => api.get('/coach/playbooks', { params }),
  get: (playbookId) => api.get(`/coach/playbooks/${playbookId}`),
  create: (data) => api.post('/coach/playbooks', data),
  update: (playbookId, data) => api.patch(`/coach/playbooks/${playbookId}`, data),
  remove: (playbookId) => api.delete(`/coach/playbooks/${playbookId}`),
};

// Rosters
export const rosterAPI = {
  list: (params = {}) => api.get('/coach/rosters', { params }),
  get: (rosterId) => api.get(`/coach/rosters/${rosterId}`),
  create: (data) => api.post('/coach/rosters', data),
  update: (rosterId, data) => api.patch(`/coach/rosters/${rosterId}`, data),
  remove: (rosterId) => api.delete(`/coach/rosters/${rosterId}`),
  addLearners: (rosterId, data) => api.post(`/coach/rosters/${rosterId}/learners`, data),
  removeLearner: (rosterId, learnerId) =>
    api.delete(`/coach/rosters/${rosterId}/learners/${learnerId}`),
};

// Audio (TTS + recordings)
export const audioAPI = {
  synthesize: (script, voice = 'alloy', speed = 1.0) =>
    api.post('/coach/audio/synthesize', { script, voice, speed }),
  upload: (file, label = '', domainId = 'football', sceneId = '', eventId = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', label);
    fd.append('domain_id', domainId);
    if (sceneId) fd.append('scene_id', sceneId);
    if (eventId) fd.append('event_id', eventId);
    return api.post('/coach/audio/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listRecordings: (params = {}) => api.get('/coach/audio/recordings', { params }),
};

// Notifications (learner + coach announcements)
export const notificationAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  createAnnouncement: (data) => api.post('/notifications/announcements', data),
  listAnnouncements: (params = {}) => api.get('/notifications/announcements', { params }),
};

export const discussionAPI = {
  listThreads: (tutorialId, sort = 'newest', limit = 20, skip = 0) =>
    api.get(`/discussions/${encodeURIComponent(tutorialId)}`, { params: { sort, limit, skip } }),
  getReplies: (threadId) => api.get(`/discussions/thread/${encodeURIComponent(threadId)}`),
  createPost: (tutorialId, bodyMd, parentId = null) =>
    api.post(`/discussions/${encodeURIComponent(tutorialId)}`, { body_md: bodyMd, parent_id: parentId }),
  editPost: (postId, bodyMd) => api.put(`/discussions/posts/${encodeURIComponent(postId)}`, { body_md: bodyMd }),
  upvote: (postId) => api.post(`/discussions/posts/${encodeURIComponent(postId)}/upvote`),
  deletePost: (postId) => api.delete(`/discussions/posts/${encodeURIComponent(postId)}`),
  getModerationQueue: (domain, status = 'pending', limit = 50) =>
    api.get('/discussions/moderation/queue', { params: { domain, status, limit } }),
  moderatePost: (postId, action, reason = null) =>
    api.put(`/discussions/moderation/${encodeURIComponent(postId)}`, { visibility: action, reason }),
};

// Phase 8 — Adaptive diagnostic helpers (coach + platform-admin)
export const getLearnerAdaptiveHealth = (learnerId, domain) =>
  api.get(`/coach/adaptive/learner/${learnerId}/health`, { params: { domain } });

export const seedLearnerReviewCards = (learnerId, domain, skillId) =>
  api.post('/coach/adaptive/seed-review-cards', { learner_id: learnerId, domain, skill_id: skillId || undefined });

export const getDomainHealth = (domain) =>
  api.get(`/coach/adaptive/domain/${domain}/health`);

export const getAdaptiveDiagnostics = () =>
  api.get('/platform/adaptive/diagnostics');

export const adaptiveAPI = {
  getOverview: (learnerId) => api.get(`/adaptive/overview/${learnerId}`),
  getMastery: (learnerId) => api.get(`/adaptive/mastery/${learnerId}`),
  getReviewQueue: (learnerId, domain) =>
    api.get(`/adaptive/review-queue/${learnerId}`, { params: domain ? { domain } : {} }),
  getReviewQueueByDomain: (learnerId) =>
    api.get(`/adaptive/review-queue/${learnerId}`, { params: { limit: 200 } }),
  getLearnerSkillMastery: (learnerId) =>
    api.get(`/adaptive/mastery/${learnerId}`),
  rateReview: (learnerId, skillId, data) =>
    api.post(`/adaptive/review/${encodeURIComponent(learnerId)}/${encodeURIComponent(skillId)}/rate`, data),
  submitLearnerEvent: (data) => api.post('/adaptive/learner-events', data),
  // CAT placement session
  startCatSession: (data) => api.post('/adaptive/cat-session', data),
  respondCatSession: (sessionId, data) =>
    api.post(`/adaptive/cat-session/${encodeURIComponent(sessionId)}/respond`, data),
  completeDiagnostic: (data) => api.post('/adaptive/diagnostic-complete', data),
  // Seed learner_mastery from level-picker selection (fixes broken level_selected no-op)
  setPlacement: (data) => api.post('/adaptive/placement', data),
  // Coach-only — GET /adaptive/review-compliance/{domain}
  getReviewCompliance: (domain = 'all') =>
    api.get(`/adaptive/review-compliance/${encodeURIComponent(domain)}`),
  // Coach-only — GET /adaptive/mastery/{learnerId} (reused for coach view)
  getLearnerMasteryForCoach: (learnerId) =>
    api.get(`/adaptive/mastery/${learnerId}`),
  // Phase 6 — CAT item bank (coach)
  getItemBank: (domain, params = {}) =>
    api.get(`/coach/adaptive/item-bank/${domain}`, { params }),
  createItemBankEntry: (domain, data) =>
    api.post(`/coach/adaptive/item-bank/${domain}`, data),
  updateItemBankEntry: (domain, itemId, data) =>
    api.put(`/coach/adaptive/item-bank/${domain}/${itemId}`, data),
  deleteItemBankEntry: (domain, itemId) =>
    api.delete(`/coach/adaptive/item-bank/${domain}/${itemId}`),
  getItemBankStats: (domain) =>
    api.get(`/coach/adaptive/item-bank/${domain}/stats`),
  // Phase 6 — Review card library (coach)
  getReviewCardLibrary: (domain, skillId) =>
    api.get(`/coach/adaptive/review-cards/${domain}/${skillId}`),
  createReviewCard: (domain, skillId, data) =>
    api.post(`/coach/adaptive/review-cards/${domain}/${skillId}`, data),
  updateReviewCard: (domain, skillId, sceneId, data) =>
    api.put(`/coach/adaptive/review-cards/${domain}/${skillId}/${sceneId}`, data),
  deleteReviewCard: (domain, skillId, sceneId) =>
    api.delete(`/coach/adaptive/review-cards/${domain}/${skillId}/${sceneId}`),
  // Phase 6 — Per-learner FSRS card health (coach)
  getLearnerCardHealth: (learnerId, domain) =>
    api.get(`/coach/adaptive/learner-cards/${learnerId}`, { params: { domain } }),
};

export const creatorAPI = {
  register: (data) => api.post('/creators', data),
  getCreator: (creatorId) => api.get(`/creators/${creatorId}`),
  updateCreator: (creatorId, data) => api.patch(`/creators/${creatorId}`, data),
  connectStripe: (creatorId) => api.post(`/creators/${creatorId}/connect-stripe`),
  submitDomain: (creatorId, data) => api.post(`/creators/${creatorId}/domains/submit`, data),
  setPricing: (creatorId, domainId, data) =>
    api.post(`/creators/${creatorId}/domains/${domainId}/set-pricing`, data),
  getEarnings: (creatorId) => api.get(`/creators/${creatorId}/earnings`),
  listDomains: (creatorId) => api.get(`/creators/${creatorId}/domains`),
};

// Learner-facing tutorials API (read-only). Coaches author tutorials at
// /coach/tutorials/*; this surface is what learners consume in the
// TutorialPlayerPage.
export const tutorialAPI = {
  list: (params = {}) => api.get('/learner/tutorials', { params }),
  get: (tutorialId) => api.get(`/learner/tutorials/${tutorialId}`),
  listTracks: (domain) => api.get('/learner/curriculum-tracks', { params: domain ? { domain } : {} }),
};

export const testPrepAPI = {
  listTests: (domain) => api.get(`/test-prep/${domain}/tests`),
  getTest: (domain, testId) => api.get(`/test-prep/${domain}/tests/${testId}`),
  startSession: (data) => api.post('/test-prep/session/start', data),
  getSession: (sessionId) => api.get(`/test-prep/session/${sessionId}`),
  submitSection: (sessionId, sectionIndex, answers) =>
    api.post(`/test-prep/session/${sessionId}/submit-section`, { section_index: sectionIndex, answers }),
  flagQuestion: (sessionId, questionId) =>
    api.post(`/test-prep/session/${sessionId}/flag`, { question_id: questionId }),
  getResults: (sessionId) => api.get(`/test-prep/session/${sessionId}/results`),
  getReview: (sessionId) => api.get(`/test-prep/session/${sessionId}/review`),
  getScoreHistory: (domain) => api.get(`/test-prep/${domain}/score-history`),
  getAnalytics: (domain) => api.get(`/test-prep/${domain}/analytics`),
  purchasePackage: (domain, packageId) =>
    api.post(`/test-prep/${domain}/purchase`, { package_id: packageId }),
  getCredits: (domain) => api.get(`/test-prep/${domain}/credits`),
};

// Email notification preferences (review_reminder is separate — on /learner/profile)
export const emailAPI = {
  getPrefs: () => api.get('/email/preferences'),
  updatePrefs: (data) => api.put('/email/preferences', data),
};

export const learnerAPI = {
  getProfile: () => api.get('/learner/profile'),
  updateProfile: (data) => api.patch('/learner/profile', data),
  getEmailPrefs: () => api.get('/email/preferences'),
  updateEmailPrefs: (data) => api.put('/email/preferences', data),
};

// Character archetype API — learner-facing character path selection.
// getCharacterClasses returns { character_archetypes_enabled, character_display_name,
//   character_classes: [{ id, display_name, description, icon }] }
// selectCharacter stores the chosen archetype for the learner in the given domain.
export const characterAPI = {
  getCharacterClasses: (domain) => api.get(`/coach/domain/${domain}/character-classes`),
  selectCharacter: (domain, characterClass) =>
    api.post('/learner/character/select', { domain, character_class: characterClass }),
};

// ── Admin scope (org/domain entitlements for current caller) ──────────────────
export const adminAPI = {
  getScope: () => api.get('/admin/scope/me'),
};

// ── Admin User Management API ─────────────────────────────────────────────────
export const adminUserAPI = {
  listUsers: (params) => api.get('/admin/users', { params }),
  getUser: (learnerId) => api.get(`/admin/users/${learnerId}`),
  createUser: (data) => api.post('/admin/users', data),
  updateProfile: (learnerId, data) => api.patch(`/admin/users/${learnerId}/profile`, data),
  resetPassword: (learnerId) => api.post(`/admin/users/${learnerId}/reset-password`),
  deactivate: (learnerId) => api.patch(`/admin/users/${learnerId}/deactivate`),
  reactivate: (learnerId) => api.patch(`/admin/users/${learnerId}/reactivate`),
  deleteUser: (learnerId, confirmEmail) => api.delete(`/admin/users/${learnerId}`, {
    params: { confirm_email: confirmEmail },
  }),
  updateDomains: (learnerId, data) => api.patch(`/admin/users/${learnerId}/domains`, data),
  assignTrack: (learnerId, data) => api.post(`/admin/users/${learnerId}/assign-track`, data),
  bulkUpload: (formData) => api.post('/admin/users/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// ── Admin Org Management API ──────────────────────────────────────────────────
export const adminOrgAPI = {
  listOrgs: (params) => api.get('/admin/orgs', { params }),
  getTree: () => api.get('/admin/orgs/tree'),
  getOrg: (orgId) => api.get(`/admin/orgs/${orgId}`),
  createOrg: (data) => api.post('/admin/orgs', data),
  updateOrg: (orgId, data) => api.patch(`/admin/orgs/${orgId}`, data),
  deleteOrg: (orgId) => api.delete(`/admin/orgs/${orgId}`),
  listMembers: (orgId, params) => api.get(`/admin/orgs/${orgId}/members`, { params }),
  addMember: (orgId, data) => api.post(`/admin/orgs/${orgId}/members`, data),
  removeMember: (orgId, userId) => api.delete(`/admin/orgs/${orgId}/members/${userId}`),
  assignCoach: (orgId, coachId, inheritDown) =>
    api.post(`/admin/orgs/${orgId}/coaches`, null, { params: { coach_id: coachId, inherit_down: inheritDown } }),
};

// ── Admin Billing API ─────────────────────────────────────────────────────────
export const adminBillingAPI = {
  listInvoices: (params) => api.get('/admin/billing', { params }),
  getInvoice: (invoiceId) => api.get(`/admin/billing/${invoiceId}`),
  generateInvoice: (data) => api.post('/admin/billing/generate', data),
  downloadPdf: (invoiceId) => api.get(`/admin/billing/${invoiceId}/pdf`, { responseType: 'blob' }),
  emailInvoice: (invoiceId) => api.post(`/admin/billing/${invoiceId}/email`),
  finalizeInvoice: (invoiceId) => api.patch(`/admin/billing/${invoiceId}/finalize`),
};

export default api;
